import React, { useState, useEffect } from "react";
import { Store, Users, Building, MapPin, Truck, Package, Award, TrendingUp } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import API_BASE_URL from '../services/apiconfig';

const Pds = () => {
  const { language } = useLanguage();
  const [pdsData, setPdsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPDSData();
  }, []);

  const fetchPDSData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/pds_master/data`);
      const result = await response.json();
      if (result && result.id) {
        setPdsData(result);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching PDS data:', error);
      setLoading(false);
    }
  };

  const iconMap = {
    Store: <Store className="w-10 h-10 text-blue-500" />,
    Users: <Users className="w-10 h-10 text-blue-500" />,
    Building: <Building className="w-10 h-10 text-blue-500" />,
    MapPin: <MapPin className="w-10 h-10 text-blue-500" />,
    Truck: <Truck className="w-10 h-10 text-blue-500" />,
    Package: <Package className="w-10 h-10 text-blue-500" />,
    Award: <Award className="w-10 h-10 text-blue-500" />,
    TrendingUp: <TrendingUp className="w-10 h-10 text-blue-500" />,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!pdsData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">No PDS data available</div>
      </div>
    );
  }

  const stats = pdsData.cards?.map(card => ({
    icon: iconMap.Store,
    title: language === 'en' ? card.header_eng : card.header_tamil,
    subtitle: language === 'en' ? card.desc_eng : card.desc_tamil,
  })) || [];

  return (
    <div className="min-h-screen bg-slate-50 font-sans">

      {/* Header */}
      <header className="text-center py-12 bg-white shadow-sm w-full">
        <h1 className="text-4xl font-bold text-gray-800">
          {language === 'en' ? 'Public Distribution System' : 'பொது விநியோக அமைப்பு'}
        </h1>
      </header>

      {/* Overview Section */}
      <section className="w-full px-6 sm:px-10 lg:px-20 py-12 bg-blue-50">
        <h2 className="text-3xl font-bold mb-4 text-gray-900">
          {language === 'en' ? 'Overview' : 'மேலோட்டம்'}
        </h2>
        <div className="border-l-4 border-blue-500 pl-6 bg-white py-4 pr-4">
          <p className="text-gray-700 leading-relaxed text-lg">
            {language === 'en' ? pdsData.overview_eng : pdsData.overview_tamil}
          </p>
        </div>
      </section>

      {/* How PDS Works */}
      <section className="w-full px-6 sm:px-10 lg:px-20 py-16 bg-white">
        <h2 className="text-center font-bold text-3xl text-gray-900 mb-12">
          {language === 'en' ? 'How PDS Works' : 'பி.டி.எஸ் எவ்வாறு செயல்படுகிறது'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            {
              num: 1,
              title: language === 'en' ? 'Procurement' : 'கொள்முதல்',
              desc: language === 'en'
                ? 'Essential commodities are sourced through cooperatives & government agencies.'
                : 'அத்தியாவசிய பொருட்கள் கூட்டுறவு மற்றும் அரசு நிறுவனங்கள் மூலம் பெறப்படுகின்றன.',
            },
            {
              num: 2,
              title: language === 'en' ? 'Allocation & Storage' : 'ஒதுக்கீடு மற்றும் சேமிப்பு',
              desc: language === 'en'
                ? 'Stocks move to district godowns & cooperative wholesale points.'
                : 'பங்குகள் மாவட்ட கிடங்குகள் மற்றும் கூட்டுறவு மொத்த விற்பனை புள்ளிகளுக்கு நகர்கின்றன.',
            },
            {
              num: 3,
              title: language === 'en' ? 'Distribution' : 'விநியோகம்',
              desc: language === 'en'
                ? '33,000+ cooperative Fair Price Shops supply essentials to families.'
                : '33,000+ கூட்டுறவு நியாய விலை கடைகள் குடும்பங்களுக்கு அத்தியாவசிய பொருட்களை வழங்குகின்றன.',
            },
            {
              num: 4,
              title: language === 'en' ? 'Monitoring' : 'கண்காணிப்பு',
              desc: language === 'en'
                ? 'Smart cards, e-POS devices & digital systems ensure transparency.'
                : 'ஸ்மார்ட் கார்டுகள், இ-பி.ஓ.எஸ் சாதனங்கள் மற்றும் டிஜிட்டல் அமைப்புகள் வெளிப்படைத்தன்மையை உறுதி செய்கின்றன.',
            },
          ].map((step) => (
            <div key={step.num} className="flex flex-col items-center text-center w-full">
              <div className="w-14 h-14 rounded-full bg-[#005598] text-white flex items-center justify-center text-2xl font-bold flex-shrink-0">
                {step.num}
              </div>
              <h3 className="text-lg font-semibold mt-4">{step.title}</h3>
              <p className="text-gray-600 mt-2 text-sm sm:text-base">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Key Stats */}
      {stats.length > 0 && (
        <section className="w-full py-10">
          <h2 className="text-3xl font-semibold mb-8 px-6 text-gray-800 text-center">
            {language === 'en' ? 'Key Stats' : 'முக்கிய புள்ளிவிவரங்கள்'}
          </h2>

          {/* Mobile: single col list */}
          <div className="sm:hidden grid grid-cols-1 gap-2 px-4 max-h-[calc(4*100px)] overflow-y-auto">
            {stats.map((item, i) => (
              <div key={i} className="bg-white rounded-xl px-4 py-5 shadow-sm flex flex-col items-center justify-center text-center min-h-[90px] border border-gray-100">
                <div className="text-xl font-black text-gray-900 leading-tight">{item.title}</div>
                <div className="text-xs text-gray-600 font-medium mt-0.5">{item.subtitle}</div>
              </div>
            ))}
          </div>

          {/* sm+: scrolling ticker */}
          <div className="hidden sm:block w-full bg-[#7EC3FF] py-6 overflow-hidden">
            <div className="flex gap-6 whitespace-nowrap animate-scroll w-max">
              {[...stats, ...stats].map((item, i) => (
                <div
                  key={i}
                  className="bg-white min-w-[300px] h-[120px] rounded-lg shadow-sm px-6 flex items-center gap-5 flex-shrink-0"
                >
                  <div className="bg-blue-50 p-4 rounded-full flex items-center justify-center">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-800 leading-tight">{item.title}</h3>
                    <p className="text-gray-600 text-base leading-snug">{item.subtitle}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <style jsx>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        .animate-scroll:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default Pds;